/**
 * Settings page — runtime-mutable application toggles.
 *
 * Each setting in the `RuntimeSettings` schema gets a row here. Adding a new
 * one means: add a field to the Python `RuntimeSettings` model, add it to
 * `types.ts`, and render a row below using either `<BoolSettingRow>` or
 * `<StringSettingRow>` (or add a new row component for other types).
 */

import { useEffect, useState } from 'react';
import { Card, Input, Switch } from '@lib/ui';
import { useSettings } from './hooks/useSettings';

export default function SettingsPage() {
  const { settings, isLoading, isSaving, error, update } = useSettings();

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Settings</h1>
        <p className="mt-1 text-gray-500 dark:text-dark-300">
          Runtime-mutable application toggles. Changes apply within ~1 minute without a redeploy.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </div>
      )}

      {isLoading || settings === null ? (
        <div className="flex items-center justify-center py-16 text-gray-400 dark:text-dark-400">
          Loading…
        </div>
      ) : (
        <Card skin="bordered" className="divide-y divide-gray-200 dark:divide-dark-600">
          <BoolSettingRow
            title="Link tracking"
            description="When enabled, iHerb posts go out with a halfnote-owned short link that records clicks before redirecting to iHerb. Disabling does not affect already-published links."
            checked={settings.link_tracking_enabled}
            disabled={isSaving}
            onChange={(checked) => {
              void update({ link_tracking_enabled: checked });
            }}
          />
          <StringSettingRow
            title="Redirect extra params"
            description="Query-string fragment merged into every redirect target. Use for an affiliate code (e.g. rcode=ABC123) or default UTMs (utm_source=halfnote&utm_medium=whatsapp). Incoming params on the tracker URL override these on key conflict."
            placeholder="rcode=ABC123"
            value={settings.redirect_extra_params}
            disabled={isSaving}
            onCommit={(value) => {
              void update({ redirect_extra_params: value });
            }}
          />
          <BoolSettingRow
            title="Auto mode"
            description="Posts skip the manual review step and go straight to APPROVED. The scheduler also tops up the queue automatically — no need to trigger generation manually."
            checked={settings.auto_mode_enabled}
            disabled={isSaving}
            onChange={(checked) => {
              void update({ auto_mode_enabled: checked });
            }}
          />
          <NumberSettingRow
            title="Auto-mode target queue size"
            description="Only used when Auto mode is on. After each scheduled send, if the APPROVED queue is below this number, the deficit is generated. 10 ≈ one day of slots."
            value={settings.auto_mode_target_count}
            min={0}
            max={50}
            disabled={isSaving || !settings.auto_mode_enabled}
            onCommit={(value) => {
              void update({ auto_mode_target_count: value });
            }}
          />
        </Card>
      )}
    </div>
  );
}

interface RowShellProps {
  title: string;
  description?: string;
  control: React.ReactNode;
}

function RowShell({ title, description, control }: RowShellProps) {
  return (
    <div className="flex items-start justify-between gap-6 p-5">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-gray-900 dark:text-white">{title}</div>
        {description && (
          <p className="mt-1 text-sm text-gray-500 dark:text-dark-300">{description}</p>
        )}
      </div>
      <div className="shrink-0">{control}</div>
    </div>
  );
}

interface BoolRowProps {
  title: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}

function BoolSettingRow({ title, description, checked, disabled, onChange }: BoolRowProps) {
  return (
    <RowShell
      title={title}
      description={description}
      control={
        <Switch
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          color="primary"
          aria-label={title}
        />
      }
    />
  );
}

interface NumberRowProps {
  title: string;
  description?: string;
  value: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  /** Fires on blur / Enter — i.e. when the user is done typing, not per keystroke. */
  onCommit: (value: number) => void;
}

function NumberSettingRow({
  title,
  description,
  value,
  min,
  max,
  disabled,
  onCommit,
}: NumberRowProps) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const parsed = Number.parseInt(draft, 10);
    if (Number.isNaN(parsed)) {
      // Reset to last good value on invalid input
      setDraft(String(value));
      return;
    }
    const clamped =
      min !== undefined && parsed < min ? min : max !== undefined && parsed > max ? max : parsed;
    if (clamped !== value) onCommit(clamped);
    setDraft(String(clamped));
  };

  return (
    <RowShell
      title={title}
      description={description}
      control={
        <Input
          type="number"
          value={draft}
          min={min}
          max={max}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.currentTarget.blur();
            }
          }}
          aria-label={title}
          className="min-w-[8rem]"
        />
      }
    />
  );
}

interface StringRowProps {
  title: string;
  description?: string;
  value: string;
  placeholder?: string;
  disabled?: boolean;
  /** Fires on blur / Enter — i.e. when the user is done typing, not per keystroke. */
  onCommit: (value: string) => void;
}

function StringSettingRow({
  title,
  description,
  value,
  placeholder,
  disabled,
  onCommit,
}: StringRowProps) {
  const [draft, setDraft] = useState(value);
  // Keep the local draft in sync if the parent value changes (e.g. after a save round-trip).
  useEffect(() => {
    setDraft(value);
  }, [value]);

  const commit = () => {
    if (draft !== value) onCommit(draft);
  };

  return (
    <RowShell
      title={title}
      description={description}
      control={
        <Input
          value={draft}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.currentTarget.blur();
            }
          }}
          aria-label={title}
          className="min-w-[20rem]"
        />
      }
    />
  );
}
